import { Jimp } from "domains/canvas/utils/jimp";

import * as Sentry from "@sentry/nextjs";

export type DataUrl = string;

export async function loadImage(data: DataUrl): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = data;
  });
}

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export async function cropAlpha(src: string): Promise<string> {
  const image = await Jimp.read(src);
  image.autocrop({});

  return image.getBase64Async(Jimp.MIME_PNG);
}

export async function getRectangleAroundNonTransparentPixels(
  image: DataUrl,
  options: { overflow?: boolean; margin?: number } = {
    overflow: false,
    margin: 0,
  }
): Promise<{
  x: number;
  y: number;
  width: number;
  height: number;
  croppedImage: DataUrl;
}> {
  // Load the image
  const imageObj = await loadImage(image);

  // Create a canvas and context
  const canvas = createCanvas(imageObj.width, imageObj.height);
  const ctx = canvas.getContext("2d");

  // Firefox throws an error if the image is not complete
  if (imageObj.width === 0) {
    void Sentry.captureEvent({
      message: "Image width is 0",
      extra: {
        imageObj,
        image,
      },
    });
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      croppedImage: "",
    };
  }

  // Draw the image onto the canvas
  ctx!.drawImage(imageObj, 0, 0, imageObj.width, imageObj.height);

  // Get the ImageData
  const imageData = ctx!.getImageData(0, 0, canvas.width, canvas.height);

  // Initialize bounds
  let minX = imageData.width,
    minY = imageData.height,
    maxX = 0,
    maxY = 0;

  // Iterate over all pixels to find bounds
  for (let y = 0; y < imageData.height; y++) {
    for (let x = 0; x < imageData.width; x++) {
      const alpha = imageData.data[(y * imageData.width + x) * 4 + 3];
      // If pixel is not transparent
      if (alpha !== 0) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    }
  }

  // Adjust bounds for margins
  if (options.margin) {
    minX = Math.max(0, minX - options.margin);
    minY = Math.max(0, minY - options.margin);
    maxX = options.overflow
      ? Math.min(imageData.width, maxX + options.margin)
      : maxX + options.margin;
    maxY = options.overflow
      ? Math.min(imageData.height, maxY + options.margin)
      : maxY + options.margin;
  }

  // Create a canvas for the cropped image
  const cropCanvas = createCanvas(maxX - minX, maxY - minY);
  const cropCtx = cropCanvas.getContext("2d");
  // Draw the cropped image onto the canvas
  cropCtx!.drawImage(
    canvas,
    minX,
    minY,
    maxX - minX,
    maxY - minY,
    0,
    0,
    maxX - minX,
    maxY - minY
  );

  // Get the data url for the cropped image
  const croppedImage = cropCanvas.toDataURL();
  // const croppedImage = await cropAlpha(image);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    croppedImage: croppedImage,
  };
}
